// ── Open-source, key-free tile layers ───────────────────────────────────────
//
// ⚠️  LICENSING NOTES:
// - OSM (OpenStreetMap): ODbL license — free for commercial + non-commercial use
//   Just requires attribution (included below)
// - CartoDB Dark (free tier): free for NON-COMMERCIAL use only
//   ⚠️  IMPORTANT: If your app is commercial, CartoDB free tier is NOT allowed.
//   You must either: (a) use CartoDB paid plan, or (b) replace with open-source like OSM
// - Esri World Imagery: Commercial use allowed with proper attribution
//   See: https://www.esri.com/en-us/legal/terms/full-master-agreement
//   Attribution required: © Esri, DigitalGlobe, Earthstar Geographics, CNES/Airbus DS
// - OpenTopoMap: CC-BY-SA license — free for all uses (commercial + non-commercial)
//   Attribution + same license on derivatives required
//
// RECOMMENDATION: For a commercial app, use OSM (light) + Esri Imagery (satellite) + OpenTopoMap.
// Avoid CartoDB free tier.

export const TILE_LAYERS = {
  current: {
    label: 'Default',
    // Default: OSM in light mode, CartoDB dark in dark mode (depends on app dark-mode setting).
    // url: null → BaseMap uses getLeafletTileConfig() which handles attribution internally.
    url: null,
    subdomains: null,
    maxZoom: 20,
    overlay: null,
    attribution: null, // Handled by getLeafletTileConfig()
  },
  satellite: {
    label: 'Satellite',
    // Esri World Imagery — free tile access, commercial use allowed with attribution.
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    subdomains: null,
    maxZoom: 20,
    overlay: null,
    attribution: '© Esri, DigitalGlobe, Earthstar Geographics, CNES/Airbus DS, &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
  // hybrid: {
  //   label: 'Hybrid',
  //   // Esri satellite + OSM street labels overlay.
  //   url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  //   subdomains: null,
  //   maxZoom: 20,
  //   overlay: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  //   overlaySubdomains: ['a', 'b', 'c'],
  //   overlayOpacity: 0.5,
  //   attribution: '© Esri, DigitalGlobe, Earthstar Geographics, CNES/Airbus DS, &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  //   overlayAttribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  // },
};

// ── App default tiles (used when TILE_LAYERS.current.url is null) ────────────
// Light: Google Maps Street (requires following Google's terms of service)
// Dark:  CartoDB dark matter (⚠️ FREE TIER IS NON-COMMERCIAL ONLY — see note above)
//        For commercial apps, replace with: https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png
export const LIGHT_TILE_URL = 'https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}'
export const LIGHT_TILE_ATTRIBUTION = '&copy; <a href="https://www.google.com/maps">Google Maps</a>, &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
export const LIGHT_TILE_SUBDOMAINS = ['mt0', 'mt1', 'mt2', 'mt3']

// Esri Dark Gray Canvas — key-free, commercial use allowed with attribution.
// No {s} subdomains (single ArcGIS host); caps at zoom ~16.
export const DARK_TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}'
export const DARK_TILE_ATTRIBUTION = '© <a href="https://www.esri.com/en-us/legal/terms/full-master-agreement">Esri</a>, HERE, Garmin, © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
export const DARK_TILE_SUBDOMAINS = []
// Dark tiles have no imagery past zoom 16, so cap zoom there.
export const DARK_TILE_MAX_ZOOM = 16

export const DEFAULT_TILE_SUBDOMAINS = ['a', 'b', 'c']

function resolveDarkMode(isDarkMode) {
  if (typeof isDarkMode === 'boolean') {
    return isDarkMode
  }

  if (typeof document === 'undefined' || !document.body) {
    return false
  }

  return document.body.classList.contains('dark-theme')
}

/**
 * Get the default tile layer config for the app (OSM in light mode, CartoDB dark in dark mode).
 * Returns { url, subdomains, attribution, maxZoom? } (maxZoom only set for dark tiles).
 *
 * ⚠️  NOTE: CartoDB dark (free tier) is non-commercial only!
 *     For commercial apps, set DARK_TILE_URL to an open-source alternative like OpenTopoMap.
 */
export function getLeafletTileConfig(isDarkMode) {
  const darkMode = resolveDarkMode(isDarkMode)

  if (darkMode) {
    return {
      url: DARK_TILE_URL,
      subdomains: DARK_TILE_SUBDOMAINS,
      attribution: DARK_TILE_ATTRIBUTION,
      maxZoom: DARK_TILE_MAX_ZOOM
    }
  }

  return {
    url: LIGHT_TILE_URL,
    subdomains: LIGHT_TILE_SUBDOMAINS,
    attribution: LIGHT_TILE_ATTRIBUTION
  }
}

/**
 * Get tile layer attribution by type.
 * For the default (current) type, this detects dark mode automatically.
 */
export function getTileLayerAttribution(tileLayerType) {
  if (!tileLayerType || tileLayerType === 'current') {
    const config = getLeafletTileConfig()
    return config.attribution
  }

  const layer = TILE_LAYERS[tileLayerType]
  return layer ? layer.attribution : null
}
